// ============================================================
// FILE: web-app/src/context/SocketContext.js
// Quản lý Socket.io connection và User Presence
// VERSION: Bỏ cảnh báo tự động - chỉ cảnh báo khi tạo đơn trùng khách hàng
// ============================================================

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import { message, Modal } from 'antd';
import { subscribePush, isPushSupported } from '../services/pushService';

const SocketContext = createContext(null);

// URL của Socket server (cùng với API)
const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || `http://${window.location.hostname}:5000`;

// Hàm phát âm thanh thông báo bằng Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Tạo 2 tiếng "ding ding" 
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      // Fade in/out để âm thanh mượt
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);        // A5 - tiếng 1
    playTone(1108, now + 0.18, 0.2); // C#6 - tiếng 2 (cao hơn)
    
  } catch (e) {
    // Audio not supported
  }
};

export const SocketProvider = ({ children, user }) => {
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [lockedRecords, setLockedRecords] = useState(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const location = useLocation();
  const currentEditingRef = useRef(null);
  const onlineUsersThrottleRef = useRef(null);
  const pendingUsersRef = useRef(null);

  // Kết nối socket khi user đăng nhập
  useEffect(() => {
    if (!user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // Gửi JWT token để server xác thực socket
      auth: {
        token: localStorage.getItem('token')
      }
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      
      // Gửi thông tin user khi kết nối
      newSocket.emit('user:join', {
        id: user.id,
        name: user.full_name || user.name || user.username,
        username: user.username,
        role: user.role
      });

      // Join room cá nhân để nhận thông báo
      newSocket.emit('join_user_room', user.id);

      // 🔔 Tự động đăng ký Push Notification
      if (isPushSupported()) {
        subscribePush().then(success => {
          if (success) console.log('🔔 Push Notification đã sẵn sàng');
        }).catch(err => console.warn('Push registration skipped:', err.message));
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', () => {});

    // Nhận danh sách user online - THROTTLE để tránh re-render liên tục
    newSocket.on('users:online', (users) => {
      pendingUsersRef.current = users;
      
      // Chỉ update state tối đa 1 lần mỗi 3 giây
      if (!onlineUsersThrottleRef.current) {
        onlineUsersThrottleRef.current = setTimeout(() => {
          if (pendingUsersRef.current) {
            setOnlineUsers(pendingUsersRef.current);
            pendingUsersRef.current = null;
          }
          onlineUsersThrottleRef.current = null;
        }, 3000);
      }
    });

    // =====================================================
    // NHẬN THÔNG BÁO CÓ ĐƠN HÀNG MỚI
    // =====================================================
    newSocket.on('order:newCreated', (data) => {
      
      // Phát âm thanh thông báo
      playNotificationSound();
      
      // Hiển thị notification
      Modal.info({
        title: '🎉 Có đơn hàng mới!',
        content: (
          <div>
            <p><strong>{data.createdBy}</strong> vừa tạo đơn hàng:</p>
            <p>📋 Số phiếu: <strong>{data.orderNumber}</strong></p>
            <p>👤 Khách hàng: <strong>{data.customerName}</strong></p>
            <p>💵 Tổng tiền: <strong style={{ color: '#f5222d' }}>
              {Number(data.totalAmount).toLocaleString('vi-VN')}đ
            </strong></p>
          </div>
        ),
        okText: 'Đã biết'
      });
    });

    // Nhận thông báo record bị lock - CHỈ LƯU TRẠNG THÁI, KHÔNG CẢNH BÁO
    newSocket.on('record:locked', (data) => {
      setLockedRecords(prev => {
        const newMap = new Map(prev);
        newMap.set(`${data.type}-${data.id}`, data.lockedBy);
        return newMap;
      });
      // ⭐ BỎ MODAL CẢNH BÁO - Không cảnh báo tự động nữa
    });

    // Nhận thông báo record được unlock
    newSocket.on('record:unlocked', (data) => {
      setLockedRecords(prev => {
        const newMap = new Map(prev);
        newMap.delete(`${data.type}-${data.id}`);
        return newMap;
      });
    });

    // ⭐ CHỈ CẢNH BÁO KHI 2 NGƯỜI CÙNG TẠO ĐƠN CHO 1 KHÁCH HÀNG
    // Event này được server gửi khi phát hiện 2 người cùng tạo đơn cho cùng khách hàng
    newSocket.on('order:conflict', (data) => {
      Modal.warning({
        title: '⚠️ Cảnh báo trùng lặp',
        content: `${data.userName} cũng đang tạo đơn cho khách hàng "${data.customerName}". Hãy phối hợp để tránh tạo đơn trùng!`,
        okText: 'Đã hiểu'
      });
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      if (onlineUsersThrottleRef.current) {
        clearTimeout(onlineUsersThrottleRef.current);
      }
    };
  }, [user?.id]); // Chỉ reconnect khi user id thay đổi

  // Gửi thông tin khi chuyển trang
  useEffect(() => {
    if (socket && isConnected && user) {
      // Nếu đang edit thì stop editing trước
      if (currentEditingRef.current) {
        socket.emit('user:stopEditing');
        currentEditingRef.current = null;
      }

      socket.emit('user:navigate', {
        page: location.pathname,
        activity: 'Đang xem'
      });
    }
  }, [location.pathname, socket, isConnected, user]);

  // Function để thông báo đang edit
  const startEditing = useCallback((type, id, name) => {
    if (socket && isConnected) {
      currentEditingRef.current = { type, id, name };
      socket.emit('user:editing', { type, id, name });
    }
  }, [socket, isConnected]);

  // Function để thông báo ngừng edit
  const stopEditing = useCallback(() => {
    if (socket && isConnected && currentEditingRef.current) {
      socket.emit('user:stopEditing');
      currentEditingRef.current = null;
    }
  }, [socket, isConnected]);

  // Kiểm tra record có bị lock không
  const isRecordLocked = useCallback((type, id) => {
    return lockedRecords.get(`${type}-${id}`);
  }, [lockedRecords]);

  // Lấy số lượng user online
  const getOnlineCount = useCallback(() => {
    return onlineUsers.length;
  }, [onlineUsers]);

  // Lấy danh sách user đang online (không tính chính mình)
  const getOtherOnlineUsers = useCallback(() => {
    if (!user) return onlineUsers;
    return onlineUsers.filter(u => u.id !== user.id);
  }, [onlineUsers, user]);

  // ⭐ Memoize context value - chỉ tạo object mới khi dependencies thực sự thay đổi
  const contextValue = useMemo(() => ({
    socket,
    isConnected,
    onlineUsers,
    lockedRecords,
    startEditing,
    stopEditing,
    isRecordLocked,
    getOnlineCount,
    getOtherOnlineUsers
  }), [socket, isConnected, onlineUsers, lockedRecords, startEditing, stopEditing, isRecordLocked, getOnlineCount, getOtherOnlineUsers]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Return default values if not in provider
    return {
      socket: null,
      isConnected: false,
      onlineUsers: [],
      lockedRecords: new Map(),
      startEditing: () => {},
      stopEditing: () => {},
      isRecordLocked: () => null,
      getOnlineCount: () => 0,
      getOtherOnlineUsers: () => []
    };
  }
  return context;
};

export default SocketContext;